import { humanoidAnimationIssues, type HumanoidAnimationAssetV1 } from "./humanoidAnimations";
import { ITEM_POSE_TYPES, type ItemPoseType } from "./characterPose";

/** V1 composes rotation-only gestures over the existing held-item evaluator.
 * Restrict the contract to capabilities actually evaluated by the runtime. */
export const COMPOSITION_MASKS = ["left-arm", "right-arm", "upper-body"] as const;
export type CompositionMask = typeof COMPOSITION_MASKS[number];
export const COMPOSITION_INTERRUPTS = ["aim", "reload", "airborne", "equipment", "action", "interaction"] as const;
export type CompositionInterrupt = typeof COMPOSITION_INTERRUPTS[number];
export interface CompositionAction {
  id: string;
  source: HumanoidAnimationAssetV1;
  mask: CompositionMask;
  /** Override local rotations; untracked bones retain the solved carry pose. */
  blend: "override";
  fadeIn: number;
  fadeOut: number;
  priority: number;
  interrupts: CompositionInterrupt[];
}
export interface AnimationCompositionV1 {
  version: 1;
  id: string;
  /** Existing controller/clip/aim strategies, preserved during migration. */
  movement: ItemPoseType;
  hold: "equipped-item";
  aim: "rifle-offset" | "item-offset";
  actions: CompositionAction[];
}

export function compositionBoneWeight(mask: CompositionMask, bone: string): number {
  // Twist helpers are derived from the final pose; accepting tracks for them
  // would promise authors a channel that the runtime immediately overwrites.
  const arm = /^(?:(?:clavicle|upperarm|lowerarm|hand)_[lr]|thumb_0[123]_[lr]|(?:index|middle|ring|pinky)_(?:metacarpal|0[123])_[lr])$/.test(bone);
  if (mask === "upper-body") return arm || /^(?:spine_0[1-5]|neck_0[12]|head)$/.test(bone) ? 1 : 0;
  const side = mask === "left-arm" ? "l" : "r";
  return arm && bone.endsWith(`_${side}`) ? 1 : 0;
}

/** Contacts follow the same envelope as their arm pose. No independent solver
 * can reclaim a hand while its action is fully weighted. */
export function compositionHands(mask: CompositionMask): readonly ("left" | "right")[] {
  return mask === "upper-body" ? ["left", "right"] : mask === "left-arm" ? ["left"] : ["right"];
}

export function animationCompositionIssues(value: unknown): string[] {
  if (!value || typeof value !== "object") return ["composition must be an object"];
  const c = value as AnimationCompositionV1;
  const issues: string[] = [];
  const id = (v: unknown): boolean => typeof v === "string" && /^[a-z0-9][a-z0-9._/-]*$/.test(v);
  if (c.version !== 1) issues.push("composition version must be 1");
  if (!id(c.id)) issues.push("composition id is invalid");
  if (!ITEM_POSE_TYPES.includes(c.movement) || c.hold !== "equipped-item"
    || (c.aim !== "item-offset" && !(c.aim === "rifle-offset" && c.movement === "rifle"))) issues.push("unsupported movement/hold/aim strategy");
  if (!Array.isArray(c.actions) || c.actions.length > 32) return [...issues, "actions must be an array of at most 32 entries"];
  const ids = new Set<string>();
  for (const a of c.actions) {
    if (!a || typeof a !== "object") { issues.push("action must be an object"); continue; }
    if (!id(a.id) || ids.has(a.id)) issues.push("action id is invalid or duplicated");
    ids.add(a.id);
    if (!COMPOSITION_MASKS.includes(a.mask)) issues.push(`${a.id}: invalid mask`);
    if (a.blend !== "override") issues.push(`${a.id}: only override blending is supported`);
    if (!Number.isInteger(a.priority) || a.priority < 0 || a.priority > 100) issues.push(`${a.id}: priority must be 0..100`);
    for (const key of ["fadeIn", "fadeOut"] as const) {
      if (!Number.isFinite(a[key]) || a[key] <= 0 || a[key] > 5) issues.push(`${a.id}: ${key} must be >0 and <=5 seconds`);
    }
    if (!Array.isArray(a.interrupts) || a.interrupts.some(v => !COMPOSITION_INTERRUPTS.includes(v))) issues.push(`${a.id}: invalid interrupts`);
    // These legacy owners have not migrated yet; never let an authored gesture
    // bypass them. Aim/reload are also mandatory for the two-hand rifle profile.
    else if (COMPOSITION_INTERRUPTS.some(v => !a.interrupts.includes(v))) issues.push(`${a.id}: rifle actions must yield to all runtime interrupts`);
    let sourceIssues: string[];
    try { sourceIssues = humanoidAnimationIssues(a.source); }
    catch { sourceIssues = ["malformed source tracks/keys"]; }
    issues.push(...sourceIssues.map(s => `${a.id}: ${s}`));
    if (sourceIssues.length) continue;
    if (a.source.rootMode !== "in-place" || a.source.loop) issues.push(`${a.id}: gesture must be an in-place one-shot`);
    if (a.fadeIn + a.fadeOut > a.source.duration) issues.push(`${a.id}: fades exceed duration`);
    if (a.source.constraints?.length || a.source.events?.length) issues.push(`${a.id}: gesture contacts/events are not supported in V1`);
    if (!Object.keys(a.source.boneTracks).length) issues.push(`${a.id}: gesture needs rotation tracks`);
    for (const [bone, track] of Object.entries(a.source.boneTracks)) {
      if (track.position?.length) issues.push(`${a.id}: gesture position tracks are not supported`);
      if (!track.rotation?.length || !compositionBoneWeight(a.mask, bone)) issues.push(`${a.id}: ${bone} is outside the mask or has no rotation`);
    }
    for (const hand of compositionHands(a.mask)) {
      const side = hand === "left" ? "l" : "r";
      if (!["upperarm", "lowerarm", "hand"].every(b => a.source.boneTracks[`${b}_${side}`]?.rotation?.length)) issues.push(`${a.id}: ${hand} contact requires a complete arm chain`);
    }
  }
  return issues;
}
